import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import {
  DataContextBuilder,
  ElderChipListSelectComponent,
  ElderDataToolbarComponent,
  ElderFormFieldDenseDirective,
  ElderFormFieldNoHintDirective,
  ElderIntervalInputComponent,
  ElderIntervalPickerBindingDirective,
  ElderIntervalPickerToggleComponent,
  ElderMultiSelectChipsComponent,
  ElderNumberCellDirective,
  ElderQuantityPipe,
  ElderQuantityService,
  ElderSelectChipAvatarDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToolbarColumnDirective,
  ElderToolbarContentDirective,
  Filter,
  IDataContext,
  Interval,
  Quantity,
  SelectionModelPopupDirective,
} from '@elderbyte/ngx-starter'
import { UtilizationReportService } from '../../workcenter-utilization/utilization-report.service'
import { ProductivityRecord, ProductivityReportService } from '../productivity-report.service'
import { sum } from '../../../../../functional.util'
import { ProcessPlanService } from '../../../process/process-plans/process-plan.service'
import { Observable } from 'rxjs'
import { ProcessPlan } from '../../../process/process-plans/process-plan'
import { WorkItemFilter } from '../../../orders/work-items/work-item.service'
import { WorkCenterService } from '../../../shopfloor/workcenters/work-center.service'
import { WorkCenter } from '../../../shopfloor/workcenters/work-center'
import { roundQuantity } from '../../reports.util'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { AsyncPipe, DatePipe } from '@angular/common'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatFooterCell,
  MatFooterCellDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatIcon } from '@angular/material/icon'
import { WorkCenterBrowserComponent } from '../../../shopfloor/workcenters/work-center-browser/work-center-browser.component'
import { MatCheckbox } from '@angular/material/checkbox'
import { FormsModule } from '@angular/forms'
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field'
import { FacilityContextDirective } from '../../../context/facility-context/facility-context.directive'
import { FacilityContextMenuComponent } from '../../../context/facility-context/facility-context-menu/facility-context-menu.component'

@Component({
  selector: 'app-productivity-report-table',
  templateUrl: './productivity-report-table.component.html',
  styleUrls: ['./productivity-report-table.component.scss'],
  imports: [
    ElderToolbarColumnDirective,
    FacilityContextMenuComponent,
    ElderTableComponent,
    FacilityContextDirective,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    MatFormField,
    ElderFormFieldDenseDirective,
    MatLabel,
    ElderIntervalInputComponent,
    FormsModule,
    MatCheckbox,
    ElderFormFieldNoHintDirective,
    ElderMultiSelectChipsComponent,
    SelectionModelPopupDirective,
    WorkCenterBrowserComponent,
    ElderSelectChipAvatarDirective,
    MatIcon,
    ElderChipListSelectComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFooterCellDef,
    MatFooterCell,
    MatSuffix,
    ElderNumberCellDirective,
    AsyncPipe,
    DatePipe,
    ElderTranslatePipe,
    ElderQuantityPipe,
    ElderIntervalPickerBindingDirective,
    ElderIntervalPickerToggleComponent,
  ],
})
export class ProductivityReportTableComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  _groupByDay: boolean
  totalPieceQuantity: number
  totalItemQuantity: number
  totalWeight: Quantity
  data: IDataContext<ProductivityRecord>
  processPlans$: Observable<ProcessPlan[]>
  selection: ProductivityRecord[] = []
  private _facility: string = null // avoid sending the string 'undefined'
  private _workCenters: WorkCenter[] = []
  private utilizationReportService = inject(UtilizationReportService)
  private quantityService = inject(ElderQuantityService)
  workCenterService = inject(WorkCenterService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const productivityReportService = inject(ProductivityReportService)
    const processPlanService = inject(ProcessPlanService)
    this.data = DataContextBuilder.start<ProductivityRecord>()
      .autoStartOnFiltersAll(['completionInterval', 'manufactureFacility', 'timeZone']) // TODO: use WorkItemFilter
      .build(productivityReportService)
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    this.data.filter.updateFilters([new Filter('timeZone', timeZone)])
    this.data.data.subscribe((records) => this.calculateTotals(records))
    this.processPlans$ = processPlanService.processPlans$
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public get completionInterval(): Interval {
    return this.utilizationReportService.completionInterval
  }

  public set completionInterval(interval: Interval) {
    this.utilizationReportService.completionInterval = interval
    const filter = new Filter('completionInterval', interval.toInstantString())
    this.data.filter.updateFilters([filter])
  }

  public get groupByDay(): boolean {
    return this._groupByDay
  }

  public set groupByDay(groupByDay: boolean) {
    const filter = new Filter('groupByDay', `${groupByDay}`)
    this.data.filter.updateFilters([filter])
    this._groupByDay = groupByDay
  }

  get facility(): string {
    return this._facility
  }

  set facility(facility: string) {
    const filter = new Filter('manufactureFacility', facility)
    this.data.filter.updateFilters([filter])
    this._facility = facility
  }

  get workCenters(): WorkCenter[] {
    return this._workCenters
  }

  set workCenters(workCenters: WorkCenter[]) {
    const ids = workCenters.map((wc) => wc.id)
    const filter = new Filter('workCenterIds', `${ids}`)
    this.data.filter.updateFilters([filter])
    this._workCenters = workCenters
  }

  calculateTotals(selected: ProductivityRecord[]) {
    this.totalPieceQuantity = selected.map((r) => r.pieceQuantity).reduce(sum, 0)
    this.totalItemQuantity = selected.map((r) => r.itemQuantity).reduce(sum, 0)
    this.totalWeight = this.quantityService.sum(selected.map((r) => r.weight))
  }

  changeProcessPlans(selected: ProcessPlan[]) {
    this.data.filter.updateFilter(WorkItemFilter.processPlans(selected))
  }

  facilitySuggestion(facility?: string) {
    return facility ? [new Filter('facility', facility)] : []
  }

  public round(pieceQuantity: number): number {
    return roundQuantity(pieceQuantity)
  }
}
